import {
  type CSSProperties,
  type TransitionEvent,
  useCallback,
  useMemo,
} from "react";
import { cn } from "../../utils";
import { useModalContext } from "./context";
import { ModalHeader } from "./ModalHeader";

export const ModalContent = () => {
  const {
    title,
    description,
    children,
    className,
    floatingContext,
    getFloatingProps,
    handleAnimationEnd,
    visible,
    titleId,
    descriptionId,
    enterDuration = 150,
    exitDuration = 100,
  } = useModalContext();

  const onTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>): void => {
      if (event.target === event.currentTarget) {
        handleAnimationEnd();
      }
    },
    [handleAnimationEnd],
  );

  const transitionStyle = useMemo((): CSSProperties => {
    return {
      transitionProperty: "opacity",
      transitionDuration: `${visible ? enterDuration : exitDuration}ms`,
      transitionTimingFunction: visible ? "ease-out" : "ease-in",
    };
  }, [visible, enterDuration, exitDuration]);

  const contentStyles = cn(
    "bg-white shadow-xl flex flex-col rounded-2xl",
    "w-full max-w-md max-h-[90vh]",
    "data-[state=open]:opacity-100",
    "data-[state=closed]:opacity-0",
    className,
  );

  return (
    <div
      ref={floatingContext.refs.setFloating}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      data-state={visible ? "open" : "closed"}
      className={contentStyles}
      style={transitionStyle}
      tabIndex={-1}
      onTransitionEnd={onTransitionEnd}
      {...getFloatingProps()}
    >
      {(title || description) && <ModalHeader />}
      {children}
    </div>
  );
};
